import { cn } from '@lib/util/cn';
import { ChevronDown } from '@modules/common/icons';
import type React from 'react';

type OptionSelectDropdownProps = {
  optionId: string;
  title: string;
  values: string[];
  current: string | undefined;
  updateOption: (optionId: string, value: string) => void;
  disabled: boolean;
  'data-testid'?: string;
};

const OptionSelectDropdown: React.FC<OptionSelectDropdownProps> = ({
  optionId,
  title,
  values,
  current,
  updateOption,
  disabled,
  'data-testid': dataTestId,
}) => {
  const selectId = `option-select-${optionId}`;
  const displayTitle = title === 'Variant' ? 'Option' : title;

  return (
    <div className="flex flex-col gap-y-1">
      <label htmlFor={selectId} className="sr-only">
        Select {displayTitle}
      </label>
      <span
        className="text-sm text-brand-primary-800"
        aria-hidden="true"
        translate="no"
      >
        {displayTitle}
      </span>
      <div className="relative" data-testid={dataTestId}>
        <select
          id={selectId}
          value={current ?? ''}
          onChange={(e) => {
            updateOption(optionId, e.target.value);
            e.currentTarget.blur();
          }}
          disabled={disabled}
          className={cn(
            'w-full h-11 appearance-none cursor-pointer',
            'border border-ui-border-base rounded-2 bg-ui-bg-subtle',
            'pl-3 pr-10 nw-typography-paragraph text-ui-fg-base',
            'focus:outline-hidden focus:ring-2 focus:ring-fg-interactive-strong focus:border-fg-interactive-strong',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          data-testid="option-select"
          translate="no"
        >
          <option value="" disabled>
            Select {displayTitle}
          </option>
          {values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-brand-primary-900"
          size="24"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default OptionSelectDropdown;
